import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  BrowserView,
  isBrowser,
  isMobile,
  MobileView,
} from "react-device-detect";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  IconButton,
  List,
  ListItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Chip,
} from "@mui/material";
import { HighlightOff, HelpOutline } from "@mui/icons-material";
import { getFileSize, isFileSizeGreaterThan } from "../../../utils/file.js";
import { getMembersApi } from "../../../api/member";
import {
  getCommentTypeApi,
  getDamagingEnergyApi,
} from "../../../api/predefine";
import {
  createNewCommentsApi,
  sendCommentNotificationApi,
  uploadNewCommentsFileApi,
} from "../../../api/comment.js";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import SelectComponent from "../../../component/SelectComponent.js";
import Popup from "../../../component/popup/Popup.js";
import UploadButton from "../../../component/button/UploadButton.js";
import ConfirmPopup from "../../../component/popup/ConfirmPopup.js";
import FullScreenLoading from "../../../component/FullScreenLoading.js";
import TooltipInfo from "../../../component/TooltipInfo.js";

const EmailTooltip = () => {
  const icon = (
    <HelpOutline
      sx={{
        position: "absolute",
        top: 0,
        left: "180px",
      }}
    />
  );
  const content = (
    <Box sx={{ p: 1 }}>
      Please click enter after entering each address.
    </Box>
  );
  return <TooltipInfo icon={icon} content={content} sx={{ width: 300, left: "140px", top: "22px" }} />;
};

const AddCommentPopup = ({
  open,
  onClose,
  selectedElement,
  onCommentAdded,
}) => {
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const [stateData, setStateData] = useState({
    categoryOptions: [],
    membersOptions: [],
    emailsOptions: [],
    membersValue: "",
    emailsValue: [],
    categoryValue: "",
    damagingEnergyList: [],
    damagingEnergyStatus: [],
    headerInput: "",
    contentInput: "",
    positiveSafety: false,
  });
  const [fileUploads, setFileUploads] = useState([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const textFieldStyle = {
    my: 0.5,
    "& .MuiInputBase-input": {
      py: 0.5,
      px: 0,
    },
    "& .MuiInputBase-root": {
      px: 2,
      py: 0,
    },
  };

  const labelStyle = {
    color: "#333",
    fontWeight: 700,
    whiteSpace: "nowrap",
  };

  const handleCheckBox = (event) => {
    const value = parseInt(event.target.value, 10);
    const { damagingEnergyStatus = [] } = stateData;
    let newStatus;

    if (event.target.checked) {
      newStatus = [...damagingEnergyStatus, value];
    } else {
      newStatus = damagingEnergyStatus.filter((item) => item !== value);
      if (newStatus.length === 0) {
        return;
      }
    }

    setStateData((prevState) => ({
      ...prevState,
      damagingEnergyStatus: newStatus,
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const newFileUploads = Array.from(files).map((file) => ({
        name: file.name,
        filesize: getFileSize(file.size),
        file: file,
      }));
      setFileUploads([...fileUploads, ...newFileUploads]);
    }
  };
  const allowedTypes = [
    "image/jpeg", "image/png", "image/gif", "image/bmp", "image/tiff", "image/webp",
    "application/pdf",
    "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ];
  const handleFileChange = (e) => {
    const files = e;
    if (!files.length) return;
    const maxSizeMB = 10;

    const newFileUploads = [];
    Array.from(files).forEach((file) => {
      const isValidSize = isFileSizeGreaterThan(file.size, maxSizeMB);
      if (isValidSize) {
        showError(`Upload Failed: File "${file.name}" exceeds the maximum size of ${maxSizeMB}MB.`);
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        showError(`Upload Failed: File "${file.name}" type is not supported.`);
        return;
      }
      newFileUploads.push({
        name: file.name,
        filesize: getFileSize(file.size),
        file: file,
      });
    });
    if (newFileUploads.length > 0) {
      setFileUploads([...fileUploads, ...newFileUploads]);
    }
  };

  const removeFile = (index) => {
    const updatedFiles = fileUploads.filter((_, i) => i !== index);
    setFileUploads(updatedFiles);
  };

  //get comment type data
  const getCommentTypeData = async () => {
    try {
      const result = await getCommentTypeApi();
      setStateData((prevState) => ({
        ...prevState,
        categoryOptions: result?.data,
      }));
    } catch {
      showError("Received comment type data occurred error.");
    }
  };

  //get DamagingEnergy data
  const getDamagingEnergyData = async () => {
    try {
      const result = await getDamagingEnergyApi();

      setStateData((prevState) => {
        const newState = {
          ...prevState,
          damagingEnergyList: result?.data,
        };

        if (
          !prevState.damagingEnergyStatus?.length &&
          result?.data?.length > 0
        ) {
          newState.damagingEnergyStatus = [result.data[0].value];
        }

        return newState;
      });
    } catch {
      showError("Received damaging energy data occurred error.");
    }
  };

  //get members data
  const getMembersData = async () => {
    try {
      const result = await getMembersApi(projectId);
      const formattedMembers = result?.data.map((member) => ({
        value: member.userId,
        label: `${member.firstName} ${member.lastName}`,
      }));
      const formattedEmails = result?.data.map((member) => ({
        value: member.email,
        label: member.email,
      }));
      setStateData((prevState) => ({
        ...prevState,
        membersOptions: formattedMembers,
        emailsOptions: formattedEmails,
      }));
    } catch {
      showError("Received user data occurred error.");
    }
  };

  const handleSubmit = async (e) => {
    setIsConfirming(false);
    setIsLoading(true);
    e.preventDefault();
    const comment = {
      elementId: selectedElement ? selectedElement.bimElementId : null,
      location: selectedElement
        ? {
          x: selectedElement.elementCenter.x || selectedElement.point.x,
          y: selectedElement.elementCenter.y || selectedElement.point.y,
          z: selectedElement.elementCenter.z || selectedElement.point.z,
        }
        : null,
      commentType: stateData.categoryValue,
      damagingEnergy:
        stateData.categoryValue === 3 ? stateData.damagingEnergyStatus : [],
      header: stateData.headerInput,
      body: stateData.contentInput,
      issueTo: stateData.membersValue,
      issueToOther: stateData.emailsValue,
      positiveSafetyObservation:
        stateData.categoryValue === 3 ? stateData.positiveSafety : null,
    };
    try {
      const response = await createNewCommentsApi(projectId, comment);
      if (response.data) {
        await Promise.all(
          fileUploads.map((f) => {
            const formData = new FormData();
            formData.append("file", f.file);
            return uploadNewCommentsFileApi(
              projectId,
              response.data.commentId,
              formData
            );
          })
        );
        await sendCommentNotificationApi(projectId, response.data.commentId);
        setStateData((prevState) => ({
          ...prevState,
          membersValue: "",
          emailsValue: [],
          categoryValue: "",
          damagingEnergyStatus: [],
          headerInput: "",
          contentInput: "",
          positiveSafety: false,
        }));
        setFileUploads([]);
        showSuccess("New Comment Added.", "assets/icons/greenaddcomment.svg");
        onCommentAdded();
      } else {
        showError("Add failed.");
      }
    } catch {
      showError("Add failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  useEffect(() => {
    getCommentTypeData();
    getDamagingEnergyData();
    getMembersData();
  }, []);

  return (
    <Popup
      open={open}
      onClose={onClose}
      noModal={isMobile}
      isExpanded={isMobile}
      popupStyle={
        isBrowser
          ? {
            minWidth: 650,
            maxWidth: 650,
          }
          : {}
      }
      paperProps={{
        component: "form",
        onSubmit: (event) => {
          event.preventDefault();
          setIsConfirming(true);
        },
      }}
      title="Add Item Comment"
    >
      <Box
        sx={{
          pt: 2,
          pb: 1,
          px: 4,
          minHeight: isBrowser ? "500px" : "100%",
          maxHeight: isBrowser ? "calc(100vh - 160px)" : "100%",
          overflow: "auto",
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <SelectComponent
              options={stateData.categoryOptions}
              value={stateData.categoryValue}
              onChange={(selectedValues) => {
                setStateData((prevState) => ({
                  ...prevState,
                  categoryValue: selectedValues,
                }));
              }}
              selectLabel="*Category"
              placeholder="Select a category..."
              selectStyle={{ width: "100%" }}
            />
          </Grid>
          {stateData.categoryValue === 3 && (
            <>
              <Grid item xs={12}>
                <Typography sx={labelStyle}>
                  Damaging Energy{" "}
                  <span style={{ fontWeight: "normal" }}>
                    (Select one or more when applicable)
                  </span>
                </Typography>
                {stateData.damagingEnergyList?.map((item) => {
                  const isChecked = (
                    stateData.damagingEnergyStatus || []
                  ).includes(item.value);
                  return (
                    <FormControlLabel
                      key={item.value}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={handleCheckBox}
                          value={item.value}
                          name={item.label}
                          color="primary"
                          sx={{ px: 0.5, py: 0.3 }}
                        />
                      }
                      label={item.label}
                      sx={{ minWidth: "110px", mr: 0.5, ml: "-6px" }}
                    />
                  );
                })}
              </Grid>
              <Grid item xs={12}>
                <Typography sx={labelStyle}>
                  Positive Safety Observation
                  <Checkbox
                    checked={stateData.positiveSafety}
                    onChange={(selectedValues) => {
                      setStateData((prevState) => ({
                        ...prevState,
                        positiveSafety: selectedValues.target.checked,
                      }));
                    }}
                    color="primary"
                    sx={{ px: 0.5, py: 0.3 }}
                  />
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography sx={labelStyle}>
              *Comment Header Input{" "}
              <span style={{ fontWeight: "normal" }}>(Max 50 characters)</span>
            </Typography>
            <TextField
              value={stateData.headerInput}
              onChange={(e) =>
                setStateData((prevState) => ({
                  ...prevState,
                  headerInput: e.target.value,
                }))
              }
              fullWidth
              placeholder="Please enter header..."
              variant="outlined"
              sx={textFieldStyle}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography sx={labelStyle}>
              *Comment Content Input{" "}
              <span style={{ fontWeight: "normal" }}>(Max 200 characters)</span>
            </Typography>
            <TextField
              value={stateData.contentInput}
              onChange={(e) =>
                setStateData((prevState) => ({
                  ...prevState,
                  contentInput: e.target.value,
                }))
              }
              multiline
              rows={6}
              fullWidth
              placeholder="Please enter comment..."
              variant="outlined"
              sx={textFieldStyle}
              inputProps={{ maxLength: 200 }}
            />
          </Grid>
          {selectedElement && (
            <Grid item xs={isBrowser ? 6 : 12}>
              <Typography sx={labelStyle}>
                Element Tagged
                <span style={{ fontWeight: "normal", marginLeft: 10 }}>
                  {selectedElement.bimElementId}
                </span>
              </Typography>
            </Grid>
          )}
          {selectedElement && (
            <Grid item xs={isBrowser ? 6 : 12}>
              <Typography sx={labelStyle}>
                Location Tagged
                <span style={{ fontWeight: "normal", marginLeft: 10 }}>
                  x:{" "}
                  {(
                    selectedElement.elementCenter.x || selectedElement.point.x
                  ).toFixed(2)}
                  , y:{" "}
                  {(
                    selectedElement.elementCenter.y || selectedElement.point.y
                  ).toFixed(2)}
                  , z:{" "}
                  {(
                    selectedElement.elementCenter.z || selectedElement.point.z
                  ).toFixed(2)}
                </span>
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <BrowserView>
              <Typography sx={labelStyle}>
                File Upload{" "}
                <span style={{ fontWeight: "normal" }}>
                  (Max upload limit 10 MB. Accepted file types: Word, Excel, PDF and images)
                </span>
              </Typography>
            </BrowserView>
            <BrowserView>
              <Box
                sx={{ border: "1px #ccc dashed", p: 2, my: 0.5 }}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <IconButton
                  component="label"
                  sx={{
                    borderRadius: 0,
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(event) => {
                      handleFileChange(event.target.files);
                    }}
                    multiple
                    style={{ display: "none" }}
                  />
                  <img
                    src="assets/buttons/uploadfile.svg"
                    alt="uploadfile"
                  ></img>
                  Click or drag to upload files
                </IconButton>
              </Box>
            </BrowserView>
            <MobileView>
              <UploadButton
                onChange={(event) => {
                  handleFileChange(event);
                }}
                label="Upload Files (Max upload limit 10 MB. Accepted file types: Word, Excel, PDF and images)"
                buttonProps={{
                  variant: "outlined",
                  sx: {
                    pr: 1.5,
                    pl: 1,
                    py: 0,
                    my: 1,
                    borderRadius: "12px",
                    width: "100%",
                    color: "#717273",
                    borderColor: "#717273",
                  },
                  startIcon: (
                    <img src="assets/icons/upload.svg" alt="Upload New File" />
                  ),
                }}
                inputProps={{ multiple: true, maxSizeMB: 10, accept: ".jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp,.pdf,.doc,.docx,.xls,.xlsx" }}
              ></UploadButton>
            </MobileView>
            <Typography sx={labelStyle}>Attached Files</Typography>
            <Box
              sx={{
                height: "100px",
                backgroundColor: "#F5F5F5",
                borderRadius: "4px",
                my: 0.5,
                px: 2,
                py: 1,
                overflow: "auto",
              }}
            >
              {fileUploads.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {fileUploads.map((file, index) => (
                    <ListItem
                      key={index}
                      sx={{ display: "flex", alignItems: "center", p: 0 }}
                    >
                      <img
                        src="assets/icons/file.svg"
                        alt="file"
                        style={{ height: 20 }}
                      ></img>
                      <Typography variant="body1" sx={{ ml: 1, mr: 2 }}>
                        {file.name} ({file.filesize})
                      </Typography>
                      <HighlightOff
                        onClick={() => removeFile(index)}
                        sx={{ cursor: "pointer" }}
                      ></HighlightOff>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body1">No files selected</Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <SelectComponent
              options={stateData.membersOptions}
              value={stateData.membersValue}
              onChange={(selectedValues) => {
                setStateData((prevState) => ({
                  ...prevState,
                  membersValue: selectedValues,
                }));
              }}
              selectLabel="*Assigned to user"
              selectStyle={{ width: "100%" }}
              placeholder="Please select users..."
            />
          </Grid>
          <Grid item xs={12} sx={{ position: "relative" }}>
            <Typography sx={labelStyle}>
              Email Issue to Other Addresses
            </Typography>
            <EmailTooltip />
            <Autocomplete
              value={stateData.emailsValue}
              onChange={(event, newValue) => {
                if (newValue.some((v) => !validateEmail(v))) {
                  setStateData((prevState) => ({
                    ...prevState,
                    invalidEmail: true,
                  }));
                } else {
                  setStateData((prevState) => ({
                    ...prevState,
                    emailsValue: newValue,
                    invalidEmail: false,
                  }));
                }
              }}
              multiple
              freeSolo
              options={stateData.emailsOptions.map((e) => e.label)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip label={option} key={key} {...tagProps} />;
                })
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Please select emails..." />
              )}
              sx={{
                my: 0.5,
                "& .MuiAutocomplete-inputRoot": { py: 0 },
                "& .MuiAutocomplete-input": { p: "4px 7px!important" },
                "& .MuiAutocomplete-tag": { height: 24 },
              }}
            ></Autocomplete>
            {stateData.invalidEmail && (
              <Typography sx={{ color: "#FF0000" }}>
                Wrong email format
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" my={1.5}>
              <Button
                type="submit"
                variant="contained"
                color={
                  stateData.categoryValue === "" ||
                    stateData.headerInput === "" ||
                    stateData.contentInput === "" ||
                    stateData.membersValue.length === 0
                    ? "primary"
                    : "success"
                }
                sx={{ borderRadius: "10px", lineHeight: "normal" }}
                disabled={
                  stateData.categoryValue === "" ||
                  stateData.headerInput === "" ||
                  stateData.contentInput === "" ||
                  stateData.membersValue.length === 0
                }
              >
                Submit
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <ConfirmPopup
        open={isConfirming}
        message="Confirm to submit new comment?"
        onCancel={() => setIsConfirming(false)}
        onConfirm={handleSubmit}
      ></ConfirmPopup>
      <FullScreenLoading isLoading={isLoading} />
    </Popup>
  );
};

export default AddCommentPopup;
